<template>
  <v-list-item
    :to="'/day_reports/' + day_report.id"
    class="day-report-list-item pa-0 my-1"
  >
    <!-- Status Avatar -->
    <template #prepend>
      <v-avatar
        :color="getStatusColor(day_report) + '-lighten-4'"
        size="48"
        class="ma-2 elevation-1"
      >
        <v-icon
          :icon="getStatusIcon(day_report)"
          :color="getStatusColor(day_report)"
        ></v-icon>
      </v-avatar>
    </template>

    <!-- Main Content -->
    <div class="py-2 flex-grow-1">
      <!-- Title Row with Type and Date Chip -->
      <div class="d-flex align-center">
        <v-list-item-title class="text-subtitle-1 font-weight-medium">
          {{ day_report.day_report_type || "Verslag" }}
        </v-list-item-title>

        <v-chip
          size="small"
          color="primary-lighten-4"
          text-color="primary-darken-1"
          class="ml-2"
          prepend-icon="mdi-calendar"
          variant="tonal"
        >
          {{ formatDate(day_report.date) }}
        </v-chip>

        <v-spacer></v-spacer>

        <!-- Status Badge -->
        <v-chip
          v-if="day_report.submitted"
          size="x-small"
          color="success-lighten-5"
          text-color="success-darken-1"
          prepend-icon="mdi-check-circle"
          variant="tonal"
          class="text-caption"
        >
          Ingediend
        </v-chip>
        <v-chip
          v-else-if="day_report.working"
          size="x-small"
          color="warning-lighten-5"
          text-color="warning-darken-1"
          prepend-icon="mdi-clock-outline"
          variant="tonal"
          class="text-caption"
        >
          In bewerking
        </v-chip>
        <v-chip
          v-else
          size="x-small"
          color="grey-lighten-3"
          text-color="grey-darken-1"
          prepend-icon="mdi-alert-circle-outline"
          variant="tonal"
          class="text-caption"
        >
          Concept
        </v-chip>
      </div>

      <!-- Time Information -->
      <div class="d-flex align-center mt-1">
        <v-icon size="small" color="primary-darken-1" class="mr-1"
          >mdi-clock-time-four-outline</v-icon
        >
        <span class="text-body-2 mr-2">{{
          formatTime(day_report.start_time)
        }}</span>

        <v-icon size="small" color="primary-darken-1" class="mr-1 ml-2"
          >mdi-timer-outline</v-icon
        >
        <span class="text-body-2">{{
          formatDurationHHMM(day_report.duration)
        }}</span>
      </div>

      <!-- User Information -->
      <div class="d-flex align-center mt-1">
        <v-avatar size="24" color="primary-lighten-4" class="mr-2">
          <span class="text-caption">{{ day_report.user_short }}</span>
        </v-avatar>
        <v-list-item-subtitle class="text-body-2">
          {{ day_report.user_name || "Onbekend" }}
        </v-list-item-subtitle>
      </div>

      <!-- Content Preview (if available) -->
      <div
        v-if="contentPreview"
        class="text-caption text-grey-darken-1 mt-1 text-truncate content-preview"
      >
        <v-icon size="x-small" color="grey" class="mr-1">mdi-text</v-icon>
        {{ contentPreview }}
      </div>
    </div>

    <!-- Context Menu and Action Icons -->
    <template #append>
      <div class="d-flex align-center">
        <!-- Attachment indicator -->
        <v-icon
          v-if="hasAttachments"
          size="small"
          color="primary"
          icon="mdi-paperclip"
          class="mr-2"
        ></v-icon>

        <!-- Comments indicator -->
        <v-icon
          v-if="hasComments"
          size="small"
          color="primary"
          icon="mdi-comment-text-outline"
          class="mr-2"
        ></v-icon>

        <!-- Account indicator -->
        <v-badge
          v-if="accountsCount > 0"
          :content="accountsCount.toString()"
          color="blue"
          offset-x="5"
          offset-y="-5"
          location="top end"
          class="mr-2"
        >
          <v-icon size="small" color="blue" icon="mdi-account-group"></v-icon>
        </v-badge>

        <ContextMenu v-if="options.length > 0" :options="options" />
      </div>
    </template>
  </v-list-item>
</template>

<script setup>
import { useFormsStore } from "@/stores/formsStore";
import { useDayReportsStoreRefactor } from "@/stores/dayReportsStoreRefactor";
import { useAuthStoreRefactor } from "@/stores/authStoreRefactor";
import { useRouter } from "vue-router";
import { useAlertStore } from "@/stores/alertStore";
import { computed } from "vue";
import moment from "moment";
import ContextMenu from "../ui/ContextMenu.vue";
import { formatDurationHHMM } from "@/composables/useDurationFormatter";

const props = defineProps({
  day_report: {
    type: Object,
    required: true,
  },
});

const router = useRouter();
const formsStore = useFormsStore();
const dayReportsStore = useDayReportsStoreRefactor();
const authStore = useAuthStoreRefactor();
const alertStore = useAlertStore();

// Computed properties for displaying data
const contentPreview = computed(() => {
  // Get content from the first line if available
  const lines = props.day_report.day_report_lines || [];
  if (lines.length > 0 && lines[0].content) {
    return (
      lines[0].content.substring(0, 80) +
      (lines[0].content.length > 80 ? "..." : "")
    );
  }
  return "";
});

const hasAttachments = computed(() => {
  return (
    props.day_report.attachments_count > 0 ||
    (props.day_report.attachments && props.day_report.attachments.length > 0)
  );
});

const hasComments = computed(() => {
  return (
    props.day_report.comments_count > 0 ||
    (props.day_report.comments && props.day_report.comments.length > 0)
  );
});

const accountsCount = computed(() => {
  if (props.day_report.accounts_count) return props.day_report.accounts_count;
  if (props.day_report.accounts && Array.isArray(props.day_report.accounts)) {
    return props.day_report.accounts.length;
  }
  return 0;
});

const options = computed(() => {
  const menuOptions = [];

  if (props.day_report.can_edit) {
    menuOptions.push({
      name: "Dagrapport wijzigen",
      action: editDayReport,
      icon: "mdi-playlist-edit",
    });
  }

  if (props.day_report.can_open) {
    menuOptions.push({
      name: "Dagrapport openen",
      action: openDayReport,
      icon: "mdi-folder-open",
    });
  }

  if (props.day_report.can_close) {
    menuOptions.push({
      name: "Dagrapport sluiten",
      action: closeDayReport,
      icon: "mdi-folder-zip",
    });
  }

  if (props.day_report.can_destroy) {
    menuOptions.push({
      name: "Dagrapport verwijderen",
      action: destroyDayReport,
      icon: "mdi-delete",
    });
  }

  return menuOptions;
});

function formatDate(date) {
  return moment(date).format("DD-MM-YYYY");
}

function formatTime(datetime) {
  if (!datetime) return "-";
  return moment(datetime).format("HH:mm");
}

function getStatusColor(report) {
  if (report.submitted) return "success";
  if (report.working) return "warning";
  return "grey";
}

function getStatusIcon(report) {
  if (report.submitted) return "mdi-check-circle";
  if (report.working) return "mdi-clock-outline";
  return "mdi-alert-circle-outline";
}

function editDayReport() {
  formsStore.edit({
    type: "day_report",
    item: props.day_report,
    options: { plan_mode: false },
  });
}

function openDayReport() {
  dayReportsStore
    .open(props.day_report)
    .then(() => {
      alertStore.success("Dagrapport succesvol geopend");
    })
    .catch(() => {
      alertStore.error("Probleem bij openen dagrapport");
    });
}

function closeDayReport() {
  console.log(
    "DayReportListItem: Attempting to close day report:",
    props.day_report.id,
  );

  dayReportsStore
    .close(props.day_report)
    .then((result) => {
      console.log("DayReportListItem: Day report closed successfully:", result);
      alertStore.success("Dagrapport succesvol gesloten");
    })
    .catch((error) => {
      console.error("DayReportListItem: Error closing day report:", error);
      if (error.response) {
        console.error("API error response:", error.response.data);
        // Display more specific error message if available
        const errorMsg =
          error.response.data?.error ||
          error.response.data?.message ||
          "Er is een probleem opgetreden bij het sluiten van het dagrapport";
        alertStore.error(errorMsg);
      } else {
        alertStore.error(
          "Er is een probleem opgetreden bij het sluiten van het dagrapport",
        );
      }
      // Removed the form opening code
    });
}

function destroyDayReport() {
  if (confirm("Weet je zeker dat je dit dagrapport wilt verwijderen?")) {
    dayReportsStore
      .destroy(props.day_report)
      .then(async () => {
        alertStore.success("Dagrapport succesvol verwijderd");

        // Refresh current user to update active_day_report_id
        await authStore.getCurrentUser(true);

        router.push("/day_reports");
      })
      .catch(() => {
        alertStore.error("Probleem bij verwijderen dagrapport");
      });
  }
}
</script>

<style scoped>
.day-report-list-item {
  border-radius: 8px;
  transition: background-color 0.2s ease;
  margin-bottom: 4px;
}

.day-report-list-item:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.content-preview {
  max-width: 90%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .content-preview {
    display: none;
  }
}
</style>
